// #region Imports
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import EditNoteIcon from "@mui/icons-material/EditNote";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import SearchIcon from "@mui/icons-material/Search";
import SnippetFolderIcon from "@mui/icons-material/SnippetFolder";
import UndoIcon from "@mui/icons-material/Undo";
import { green } from "@mui/material/colors";
import { keyframes } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import { alpha, styled, useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { skipToken } from "@reduxjs/toolkit/query";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleBillingCode from "_shared/fee-schedules/categories/billing-codes/fee-schedule-billing-code";
import FeeScheduleCategoryDescription from "_shared/fee-schedules/categories/descriptions/fee-schedule-category-description";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import Fee from "_shared/fee-schedules/categories/fees/fee";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import Guid from "_shared/guids/guid";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import {
  createMRTColumnHelper,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_Row,
  type MRT_SortingState as SortingState,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { FeeScheduleCategoryMaskedCurrency } from "work/masked-fields";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import {
  proposalCollectionItemRevertAspect,
  proposalCollectionItemRevertValue,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import { proposalFeeScheduleAspectOptions } from "work/proposals/negotiation/proposal-fee-schedule-aspects";
import { useRemovedProposalFeeScheduleCategories } from "work/proposals/negotiation/proposal-fee-schedule-removed-categories";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import FeeRedlineInput from "work/proposals/redlining/fee-schedule-redline/fee-redline/_views/fee-redline-input";
import { FeeScheduleCategoryRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-category-redline";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import TextRedlineInput from "work/proposals/redlining/field-redlines/_views/text-redline-input";
import FieldRedline from "work/proposals/redlining/field-redlines/field-redline";
import { MRTNoMemo } from "_shared/_views/tables/table";
import TableAddActions from "_shared/_views/tables/table-add-actions";
import {
  CollectionToolbar,
  CollectionToolbarSearch,
  CollectionToolbarViewButtons,
} from "_shared/_views/tables/table-toolbar";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import {
  FeeScheduleTableCellText,
  feeScheduleTableCellStyles,
} from "_shared/fee-schedules/categories/_views/fee-schedule-table-cell";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import {
  paneIsCompact,
} from "work/_views/work-pane-layout";
import {
  useWorkPaneIsCompact,
} from "work/_views/use-work-pane-size";
// #endregion

// #region Styles
const pulseRowOutline = keyframes`
  0%, 100% {
    border-color: rgba(21, 101, 192, 0.28);
  }
  50% {
    border-color: rgba(21, 101, 192, 0.75);
  }
`;
const RowActionsContainer = styled("div")(() => ({
  whiteSpace: "nowrap",
}));
const RedlineText = styled(TextRedlineInput)(() => ({
  margin: 0,
  minWidth: "unset",
  width: "100%",
}));
const LockedCellContent = styled("span")({
  alignItems: "center",
  display: "inline-flex",
  justifyContent: "center",
  lineHeight: 0,
  verticalAlign: "middle",
  width: "100%",
});
const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));
const TeamMemberCell = styled("div")({
  alignItems: "center",
  display: "flex",
  gap: 4,
  minWidth: 0,
  width: "100%",
});
const TeamMemberEditButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(0.25),
}));
/**
 * The member a removed category was assigned to. The category's other cells are already struck through
 * by their own redlines; this one had gone blank instead, which read as "never assigned" rather than
 * "removed along with the rest of the row".
 */
const RemovedTeamMember = styled("span")(({ theme }) => ({
  color: theme.palette.text.disabled,
  display: "block",
  minWidth: 0,
  textDecoration: "line-through",
  textDecorationColor: theme.palette.text.primary,
  textDecorationThickness: "2px",
  width: "100%",
}));
const TeamMemberValue = styled("span")({
  cursor: "text",
  display: "block",
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: "inherit",
  lineHeight: "inherit",
  minWidth: 0,
  textAlign: "left",
  width: "100%",
});
const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
  minWidth: 0,
  overflow: "hidden",
  position: "relative",
});
const LOCKED_COLUMN_WIDTH = 40;
/**
 * How wide a category description is allowed to get before it is cut off.
 *
 * Descriptions are free text and routinely run to a paragraph; left unbounded they pushed the fee and
 * billing code off the visible row entirely (#4490). The full text is one hover away — the cell
 * measures its own truncation and only then offers the tooltip.
 */
const DESCRIPTION_MAX_WIDTH = 280;

/** What the schedule's add control calls its two ways of gaining a category. */
export const ADD_NEW_CATEGORY_LABEL = "Add New Category";
export const ADD_EXISTING_CATEGORY_LABEL = "Add Existing Category";

const ClampedDescription = styled("div", { shouldForwardProp: (prop) => prop !== "clamped" })<{ clamped: boolean }>(
  ({ clamped }) => ({
    minWidth: 0,
    width: "100%",
    ...(clamped && {
      maxWidth: DESCRIPTION_MAX_WIDTH,
      // Stated on the wrapper as well as on the text inside it. The wrapper is what a reader's pointer
      // is over, and what the measured tooltip below asks whether the text is cut off in.
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      "& .MuiInputBase-input": {
        overflow: "hidden",
      },
      // ONE line, with an ellipsis, exactly as every other cell in these tables reads. It used to be a
      // two-line `-webkit-box` clamp, which is the multi-line wrapping #4955's review asked to be taken
      // off the collection tables: a row whose height depends on how much someone wrote is a row that
      // cannot be scanned against its neighbours. The whole description is still one hover away, and
      // the column can be dragged wider or double-clicked open (#4955).
      "& .MuiInputBase-input > div, & .MuiTypography-root": {
        lineHeight: "inherit",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    }),
  }),
);

function ClampedDescriptionField({
  children,
  clamped,
  title,
}: Readonly<{ children: ReactNode; clamped: boolean; title: string }>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const updateTooltip = () => {
    const root = rootRef.current;
    const clippedElements = root
      ? [
        root,
        ...root.querySelectorAll<HTMLElement>(
          ".MuiInputBase-input > div, .MuiTypography-root, .MuiInputBase-input",
        ),
      ]
      : [];
    setTooltipOpen(Boolean(
      clamped && title && clippedElements.some((element) =>
        element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight)
    ));
  };

  return (
    <Tooltip
      open={tooltipOpen}
      title={title}
      placement="top-start"
      onClose={() => setTooltipOpen(false)}
      disableFocusListener
      disableTouchListener
      slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
    >
      <ClampedDescription
        ref={rootRef}
        clamped={clamped}
        onMouseEnter={updateTooltip}
        onMouseLeave={() => setTooltipOpen(false)}
      >
        {children}
      </ClampedDescription>
    </Tooltip>
  );
}
// #endregion

// #region Types/Props
type FeeScheduleRedlineSelectionProps = {
  className?: string;
  feeScheduleRedline: FeeScheduleRedline;
  teamMembers: readonly EntityMember[];
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  negotiationId?: string;
  viewerScope?: string;
  activeReviewField: ProposalField | undefined;
  onCategoryClicked?: (categoryId: Guid) => void;
  onCreateCategoryClicked: () => void;
  onFeeScheduleRedlineChange: (
    newRedline: FeeScheduleRedline,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onExistingCategoryClicked: () => void;
};
// #endregion

const FeeScheduleRedlineSelection = (props: Readonly<FeeScheduleRedlineSelectionProps>) => {
  const {
    className,
    feeScheduleRedline,
    teamMembers,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    activeReviewField,
    onCategoryClicked,
    onCreateCategoryClicked,
    onExistingCategoryClicked,
    onFeeScheduleRedlineChange,
  } = props;

  const theme = useTheme();
  // The table drops columns by how much room the pane has, not how wide the window is (#4964).
  const isMediumDisplaySize = useWorkPaneIsCompact();
  const [query, setQuery] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  // The category whose description is open for editing. Its cell drops the clamp and the tooltip so
  // the writer sees what they are typing.
  const [editingDescriptionRowId, setEditingDescriptionRowId] = useState<string | null>(null);
  const [editingTeamMemberRowId, setEditingTeamMemberRowId] = useState<string | null>(null);
  const [openTeamMemberMenuRowId, setOpenTeamMemberMenuRowId] = useState<string | null>(null);

  // #region Removed categories
  // The categories the schedule no longer has, kept as struck-through rows of their own so each keeps
  // the history of the category it was (#4951). They are not part of `feeScheduleRedline` — nothing
  // here edits, resolves or undoes them — so every control that writes to the redline looks them up by
  // id and steps aside. A category the redline itself still holds as a removal (open for undo, or
  // awaiting a decision) is already a row, and is not repeated.
  const presentCategoryIds = useMemo(
    () => feeScheduleRedline.redlines.flatMap((redline) => (redline.field.id ? [redline.field.id.value] : [])),
    [feeScheduleRedline],
  );
  const removedCategories = useRemovedProposalFeeScheduleCategories({
    negotiationId,
    viewerScope,
    presentItemIds: presentCategoryIds,
  });
  const removedCategoryRows = useMemo(
    () =>
      removedCategories.map(
        // Original present, revised and current absent: the shape of an accepted removal, which the cells
        // already draw struck through.
        (removed) => new FeeScheduleCategoryRedline(removed.category.id, removed.category, null, null),
      ),
    [removedCategories],
  );
  const removedCategoryIds = useMemo(
    () => new Set(removedCategoryRows.flatMap((row) => (row.field.id ? [row.field.id.value] : []))),
    [removedCategoryRows],
  );
  const isRemovedCategoryRow = useCallback(
    (row: FeeScheduleCategoryRedline) => Boolean(row.field.id && removedCategoryIds.has(row.field.id.value)),
    [removedCategoryIds],
  );
  const rows = useMemo(
    () => [...feeScheduleRedline.redlines, ...removedCategoryRows],
    [feeScheduleRedline.redlines, removedCategoryRows],
  );
  // #endregion

  // #region Refs
  const prevVisibilityRef = useRef<Record<string, boolean> | null>(null);
  const prevSizingRef = useRef<Record<string, number> | null>(null);
  // The container the table scrolls in. The scrollbar on it is `MRTNoMemo`'s, and it is what says the
  // rows continue past the right-hand edge — the shaded edges and arrows that used to say so are gone
  // (#4955).
  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // #region Lifecycle
  useEffect(() => {
    const target = document.getElementById(activeReviewField?.id?.value ?? "");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeReviewField]);

  // #endregion

  // #region Functions
  const handleRemoveClicked = useCallback(
    (categoryId: Guid) => {
      const newRedline = feeScheduleRedline.removeEntryByFieldId(categoryId);
      onFeeScheduleRedlineChange(newRedline, newRedline.field);
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // A revert restores a prior category value through the SAME `replaceEntryById` edit path any other
  // cell edit uses (renderNameRedline/renderFeeRedline/…), so it records an ordinary redline "Edit" and
  // flows to `queueProposalUpdate` as a `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw
  // `FieldSet` (see proposal-collection-item-revert.ts). Because it lands as an Edit in the redline's
  // session history exactly like a manual edit would, the save-time resolution-decision builder
  // (proposal-resolution-decision-builder.ts) classifies it as `AcceptIncoming`/`KeepPrevious`/
  // `ProposeAlternative` from the *resulting* materialized value automatically — no separate handling
  // is needed here to satisfy the "revert must be a resolution decision" rule.
  const handleCategoryRevert = useCallback(
    (categoryFieldId: Guid, entry: ProposalFieldTimelineRevertEntry) => {
      // This dropdown instance is always in collection-item mode (a fee-schedule category), so
      // `entry.operation` is always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      // A single-column revert (#4847) restores one property and must leave the rest of the category
      // alone — including any column the counterparty has since changed. So it is merged onto the
      // category as it currently stands rather than replacing it; a whole-entry revert still replaces.
      const revertedAspect = proposalCollectionItemRevertAspect(entry.operation);
      const currentCategory = feeScheduleRedline.redlines.find((redline) =>
        redline.field.id?.isEqualTo(categoryFieldId),
      );
      const currentJSON = (currentCategory?.currentEntry ?? currentCategory?.revisedEntry)?.toJSON();
      // One column on its own is not a category, so without the category to merge it into there is
      // nothing to restore — building one from the fragment would fail schema validation inside a click.
      if (revertedAspect && !currentJSON) return;
      const revertedJSON = revertedAspect
        ? { ...currentJSON, ...(revertValue as Partial<NonNullable<typeof currentJSON>>) }
        : revertValue;
      const revertedCategory = FeeScheduleCategory.fromJSON(revertedJSON as never);
      if (!revertedCategory) return;
      const newRedline = feeScheduleRedline.replaceEntryById(categoryFieldId, revertedCategory);
      onFeeScheduleRedlineChange(newRedline, ProposalField.FeeScheduleCategory(categoryFieldId));
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  /**
   * Talks the schedule back into a category it no longer has.
   *
   * A tombstone is outside `feeScheduleRedline` — its id is absent from the collection, which is the
   * whole reason it is a tombstone — so the way back is `addEntry`, not `replaceEntryById`, which
   * throws on an id it cannot find. `addEntry` takes the same edit path any other category addition
   * uses, so the restore diffs to a `CollectionItemAdded` through `buildCollectionOperations` with no
   * wire work of its own, and never a raw `FieldSet` — the same rule a revert follows, and for the
   * same reason (`proposal-collection-item-revert.ts`: when the item's most recent event was its
   * removal there is nothing to update, and restoring it is an add).
   *
   * The row then evicts itself: the restored id joins `presentCategoryIds`, so the removed-categories
   * reading stops listing it and the live redline row takes its place.
   */
  const handleRestoreClicked = useCallback(
    (category: FeeScheduleCategoryEntry) => {
      if (!category) return;
      onFeeScheduleRedlineChange(feeScheduleRedline.addEntry(category), ProposalField.FeeScheduleCategory(category.id));
    },
    [feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  const enterFocusMode = useCallback((table: MRT_TableInstance<FeeScheduleCategoryRedline>, columnId: string) => {
    const tableState = table.getState();

    // Snapshot existing state
    prevVisibilityRef.current ??= { ...tableState.columnVisibility };
    prevSizingRef.current ??= { ...tableState.columnSizing };

    const keepVisible = new Set<string>([columnId, "mrt-row-actions", "name"]);

    // Build column visibility map
    const nextVisibility: Record<string, boolean> = {};
    table.getAllLeafColumns().forEach((col) => {
      nextVisibility[col.id] = keepVisible.has(col.id);
    });

    table.setColumnVisibility(nextVisibility);
  }, []);

  const exitFocusMode = useCallback((table: MRT_TableInstance<FeeScheduleCategoryRedline>) => {
    if (prevVisibilityRef.current) {
      table.setColumnVisibility(prevVisibilityRef.current);
      prevVisibilityRef.current = null;
    }
    if (prevSizingRef.current) {
      table.setColumnSizing(prevSizingRef.current);
      prevSizingRef.current = null;
    }
  }, []);
  // #endregion

  // #region Billing Code Cell
  const renderBillingCodeRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.billingCodeRedline) {
        console.error("Category Redline billing code is malformed", categoryRedline);
        return;
      }
      return (
        <RedlineText
          variant="standard"
          size="small"
          margin="none"
          disableFullWidth
          hideLabel
          showPlaceholder
          textRedline={categoryRedline.billingCodeRedline}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          hideFieldHistory={true}
          fieldConstructor={FeeScheduleBillingCode}
          onShowTextInput={() => enterFocusMode(table, "billingCode")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={async (
            newTextRedline: FieldRedline<FeeScheduleBillingCode>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                  name: existingCategory.name,
                  description: existingCategory.description,
                  fee: existingCategory.fee,
                  billingCode: newTextRedline.currentEntry,
                  id: existingCategory.id,
                  teamMember: existingCategory.teamMember,
                  teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                })
                : new FeeScheduleCategoryMaskedCurrency({
                  id: existingCategory.id,
                  name: existingCategory.name,
                  description: existingCategory.description,
                  billingCode: newTextRedline.currentEntry,
                  teamMember: existingCategory.teamMember,
                  teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                });
            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
          }}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // #region Description Cell
  const renderDescriptionRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.descriptionRedline) {
        console.error("Category Redline description is malformed", categoryRedline);
        return;
      }
      const rowId = rowData.field.id.value;
      const isEditingDescription = editingDescriptionRowId === rowId;
      const fullDescription = isEditingDescription ? "" : fullDescriptionText(categoryRedline);
      return (
        <ClampedDescriptionField
          title={fullDescription}
          clamped={!isEditingDescription}
        >
          <RedlineText
              variant="standard"
              size="small"
              margin="none"
              disableFullWidth
              hideLabel
              showPlaceholder
              hideUndoButton={true}
              hideAcceptRejectButtons={true}
              textRedline={categoryRedline.descriptionRedline}
              fieldConstructor={FeeScheduleCategoryDescription}
              onShowTextInput={() => {
                setEditingDescriptionRowId(rowId);
                enterFocusMode(table, "description");
              }}
              onHideTextInput={() => {
                setEditingDescriptionRowId((editingRowId) => (editingRowId === rowId ? null : editingRowId));
                exitFocusMode(table);
              }}
              onTextFieldRedlineChange={async (
                newTextRedline: FieldRedline<FeeScheduleCategoryDescription>,
                traversalFieldOverride?: ProposalField | null,
              ) => {
                const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
                if (!existingCategory || !newTextRedline.currentEntry) {
                  console.error("category redline is malformed", categoryRedline);
                  return;
                }
                const updatedCategory =
                  existingCategory instanceof FeeScheduleCategory
                    ? FeeScheduleCategory.create({
                      name: existingCategory.name,
                      description: newTextRedline.currentEntry,
                      fee: existingCategory.fee,
                      billingCode: existingCategory.billingCode,
                      id: existingCategory.id,
                      teamMember: existingCategory.teamMember,
                      teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                    })
                    : new FeeScheduleCategoryMaskedCurrency({
                      id: existingCategory.id,
                      name: existingCategory.name,
                      description: newTextRedline.currentEntry,
                      billingCode: existingCategory.billingCode,
                      teamMember: existingCategory.teamMember,
                      teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                    });

                const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
                onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
              }}
              readOnly={disableEditing || categoryRedline.isRemoved}
              hideFieldHistory={true}
          />
        </ClampedDescriptionField>
      );
    },
    [
      disableEditing,
      editingDescriptionRowId,
      enterFocusMode,
      exitFocusMode,
      feeScheduleRedline,
      onFeeScheduleRedlineChange,
    ],
  );

  // #region Fee Cell
  const renderFeeRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (
        rowData.originalEntry instanceof FeeScheduleCategoryMaskedCurrency ||
        rowData.revisedEntry instanceof FeeScheduleCategoryMaskedCurrency
      ) {
        return <em>Masked</em>;
      }
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.feeRedline) {
        console.error("Category Redline fee is malformed", categoryRedline);
        return;
      }

      return (
        <FeeRedlineInput
          variant="standard"
          size="small"
          disableFullWidth
          margin="none"
          categoryRedline={categoryRedline}
          readOnly={disableEditing || categoryRedline.isRemoved}
          onShowTextInput={() => enterFocusMode(table, "fee")}
          onHideTextInput={() => exitFocusMode(table)}
          onFeeChange={(newFee: Fee) => {
            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                  name: existingCategory.name,
                  description: existingCategory.description,
                  fee: newFee,
                  billingCode: existingCategory.billingCode,
                  id: existingCategory.id,
                  teamMember: existingCategory.teamMember,
                  teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                })
                : new FeeScheduleCategoryMaskedCurrency({
                  id: existingCategory.id,
                  name: existingCategory.name,
                  description: existingCategory.description,
                  billingCode: existingCategory.billingCode,
                  teamMember: existingCategory.teamMember,
                  teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline);
          }}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // #region Name Cell
  const renderNameRedline = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) {
        console.error("Category Redline ID is malformed", rowData);
        return;
      }
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      if (!categoryRedline.nameRedline) {
        console.error("Category Redline Name is malformed", categoryRedline);
        return;
      }
      return (
        <RedlineText
          id={categoryRedline.field.id?.value}
          bold={true}
          disableFullWidth
          variant="standard"
          size="small"
          margin="none"
          hideLabel
          showPlaceholder
          hideUndoButton={true}
          hideAcceptRejectButtons={true}
          textRedline={categoryRedline.nameRedline}
          fieldConstructor={FeeScheduleCategoryName}
          onShowTextInput={() => enterFocusMode(table, "name")}
          onHideTextInput={() => exitFocusMode(table)}
          onTextFieldRedlineChange={(
            newTextRedline: FieldRedline<FeeScheduleCategoryName>,
            traversalFieldOverride?: ProposalField | null,
          ) => {
            const duplicateName = feeScheduleRedline.redlines.find((redline) => {
              if (!redline.nameRedline) {
                console.error("Category Redline Name is malformed", redline);
                return false;
              }
              return redline.nameRedline.currentEntry === newTextRedline.currentEntry;
            });
            if (duplicateName) {
              enqueueSnackbar("A category with the same name already exists", {
                variant: "warning",
              });
              return;
            }

            const existingCategory = categoryRedline.currentEntry ?? categoryRedline.revisedEntry;
            if (!existingCategory || !newTextRedline.currentEntry) {
              console.error("category redline is malformed", categoryRedline);
              return;
            }
            const updatedCategory =
              existingCategory instanceof FeeScheduleCategory
                ? FeeScheduleCategory.create({
                  id: existingCategory.id,
                  name: newTextRedline.currentEntry,
                  description: existingCategory.description,
                  fee: existingCategory.fee,
                  billingCode: existingCategory.billingCode,
                  teamMember: existingCategory.teamMember,
                  teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                })
                : new FeeScheduleCategoryMaskedCurrency({
                  id: existingCategory.id,
                  name: newTextRedline.currentEntry,
                  description: existingCategory.description,
                  billingCode: existingCategory.billingCode,
                  teamMember: existingCategory.teamMember,
                  teamMemberIsMasked: existingCategory.teamMemberIsMasked,
                });

            const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
            onFeeScheduleRedlineChange(newRedline, traversalFieldOverride);
          }}
          readOnly={disableEditing || categoryRedline.isRemoved}
          hideFieldHistory={true}
        />
      );
    },
    [disableEditing, enterFocusMode, exitFocusMode, feeScheduleRedline, onFeeScheduleRedlineChange],
  );

  // #region Columns
  const columnHelper = useMemo(() => createMRTColumnHelper<FeeScheduleCategoryRedline>(), []);
  const teamMemberUserIds = useMemo(
    () => new Set(
      [
        ...teamMembers.map((member) => member.userId.value),
        ...rows.flatMap((redline) => {
          const category = displayedCategory(redline);
          return category?.teamMember && !category.teamMemberIsMasked
            ? [category.teamMember.userId.value]
            : [];
        }),
      ],
    ),
    [rows, teamMembers],
  );
  const teamMemberIndividualsQuery = useGetIndividualsByUserIdsQuery(
    teamMemberUserIds.size > 0 ? { userIds: teamMemberUserIds } : skipToken,
  );
  const teamMemberNames = useMemo(
    () => new Map(
      (Individual.fromJSONArray(teamMemberIndividualsQuery.data, { allowEmpty: true }) ?? []).map((individual) => [
        individual.userId.value,
        individual.fullName,
      ]),
    ),
    [teamMemberIndividualsQuery.data],
  );
  const teamMemberOptions = useMemo(
    () => teamMembers.map((member) => ({
      label: teamMemberNames.get(member.userId.value) ?? "Team member",
      member,
      value: teamMemberValue(member),
    })),
    [teamMemberNames, teamMembers],
  );
  const closeTeamMemberEditor = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>) => {
      setOpenTeamMemberMenuRowId(null);
      setEditingTeamMemberRowId(null);
      exitFocusMode(table);
    },
    [exitFocusMode],
  );
  const renderTeamMember = useCallback(
    (table: MRT_TableInstance<FeeScheduleCategoryRedline>, rowData: FeeScheduleCategoryRedline) => {
      if (!rowData.field.id) return null;
      const categoryRedline = Object.assign(new FeeScheduleCategoryRedline(rowData.field.id, null, null), rowData);
      const category = displayedCategory(categoryRedline);
      if (!category) return null;

      const displayName = category.teamMemberIsMasked
        ? "Restricted"
        : category.teamMember
          ? teamMemberNames.get(category.teamMember.userId.value) ?? "Team member"
          : "Unassigned";
      // A removed category's member goes with it: struck through like the rest of the row.
      if (categoryRedline.isRemoved) {
        return (
          <TeamMemberCell>
            <RemovedTeamMember>
              <FeeScheduleTableCellText title={displayName}>{displayName}</FeeScheduleTableCellText>
            </RemovedTeamMember>
          </TeamMemberCell>
        );
      }
      const isConcreteCategory = category instanceof FeeScheduleCategory;
      const canAssignTeamMember = Boolean(
        !disableEditing &&
        !categoryRedline.isRemoved &&
        isConcreteCategory &&
        !category.teamMemberIsMasked &&
        category.fee?.unit &&
        (teamMemberOptions.length > 0 || category.teamMember),
      );
      const isEditing = canAssignTeamMember && editingTeamMemberRowId === rowData.field.id.value;

      if (!isEditing || !(category instanceof FeeScheduleCategory)) {
        const openTeamMemberEditor = () => {
          const rowId = rowData.field.id!.value;
          setEditingTeamMemberRowId(rowId);
          setOpenTeamMemberMenuRowId(rowId);
          enterFocusMode(table, "teamMember");
        };
        const handleTeamMemberClick = (event: ReactMouseEvent<HTMLElement>) => {
          event.stopPropagation();
          openTeamMemberEditor();
        };
        const handleTeamMemberKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          event.stopPropagation();
          openTeamMemberEditor();
        };
        const teamMemberValueDisplay = (
          <FeeScheduleTableCellText title={displayName}>{displayName}</FeeScheduleTableCellText>
        );
        return (
          <TeamMemberCell>
            {canAssignTeamMember ? (
              <TeamMemberValue
                aria-label={`Edit team member: ${displayName}`}
                role="button"
                tabIndex={0}
                onClick={handleTeamMemberClick}
                onKeyDown={handleTeamMemberKeyDown}
              >
                {teamMemberValueDisplay}
              </TeamMemberValue>
            ) : teamMemberValueDisplay}
            {canAssignTeamMember && (
              <TeamMemberEditButton
                aria-label="Edit team member"
                size="small"
                onClick={handleTeamMemberClick}
              >
                <EditNoteIcon color="primary" fontSize="small" />
              </TeamMemberEditButton>
            )}
          </TeamMemberCell>
        );
      }

      const selectedValue = category.teamMember ? teamMemberValue(category.teamMember) : "";
      const selectedMemberIsAvailable = category.teamMember
        ? teamMembers.some((member) => member.isEqualTo(category.teamMember))
        : true;

      const updateTeamMember = (event: SelectChangeEvent<string>) => {
        const selectedMember = event.target.value
          ? teamMembers.find((member) => teamMemberValue(member) === event.target.value) ?? null
          : null;
        if (
          (selectedMember === null && !category.teamMember) ||
          selectedMember?.isEqualTo(category.teamMember)
        ) {
          closeTeamMemberEditor(table);
          return;
        }

        const updatedCategory = new FeeScheduleCategory({
          id: category.id,
          name: category.name,
          description: category.description,
          fee: category.fee,
          billingCode: category.billingCode,
          teamMember: selectedMember,
          teamMemberIsMasked: category.teamMemberIsMasked,
          version: category.version,
        });
        const newRedline = feeScheduleRedline.replaceEntryById(updatedCategory.id, updatedCategory);
        onFeeScheduleRedlineChange(newRedline, categoryRedline.field);
        closeTeamMemberEditor(table);
      };

      return (
        <ClickAwayListener onClickAway={() => closeTeamMemberEditor(table)}>
          <TeamMemberCell
            onBlurCapture={(event) => {
              const nextFocus = event.relatedTarget;
              if (
                nextFocus == null && openTeamMemberMenuRowId === rowData.field.id!.value ||
                nextFocus instanceof Element &&
                (event.currentTarget.contains(nextFocus) || nextFocus.closest('[role="listbox"]'))
              ) return;
              closeTeamMemberEditor(table);
            }}
          >
            <Select<string>
              aria-label="Team member"
              fullWidth
              open={openTeamMemberMenuRowId === rowData.field.id.value}
              size="small"
              value={selectedValue}
              onChange={updateTeamMember}
              onOpen={() => setOpenTeamMemberMenuRowId(rowData.field.id!.value)}
              onClose={() => setOpenTeamMemberMenuRowId(null)}
              onClick={(event) => event.stopPropagation()}
            >
              <MenuItem value="">Unassigned</MenuItem>
              {!selectedMemberIsAvailable && category.teamMember && (
                <MenuItem disabled value={selectedValue}>{displayName}</MenuItem>
              )}
              {teamMemberOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
            <Tooltip title="Cancel">
              <TeamMemberEditButton
                aria-label="Cancel"
                color="inherit"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  closeTeamMemberEditor(table);
                }}
              >
                <CloseIcon fontSize="small" />
              </TeamMemberEditButton>
            </Tooltip>
          </TeamMemberCell>
        </ClickAwayListener>
      );
    },
    [
      closeTeamMemberEditor,
      disableEditing,
      editingTeamMemberRowId,
      enterFocusMode,
      feeScheduleRedline,
      onFeeScheduleRedlineChange,
      openTeamMemberMenuRowId,
      teamMemberNames,
      teamMemberOptions,
      teamMembers,
    ],
  );
  const columns = useMemo(
    () => [
      ...(disableEditing && fieldLockTooltip
        ? [
          columnHelper.display({
            header: "",
            id: "locked",
            size: LOCKED_COLUMN_WIDTH,
            enableColumnActions: false,
            enableSorting: false,
            muiTableBodyCellProps: {
              align: "center",
              sx: {
                verticalAlign: "middle",
              },
            },
            Cell: () => (
              <LockedCellContent>
                <ReviewerEditLockIcon title={fieldLockTooltip} />
              </LockedCellContent>
            ),
          }),
        ]
        : []),
      columnHelper.accessor(getCategoryRedlineNameValue, {
        header: "Name",
        id: "name",
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ table, row }) => renderNameRedline(table, row.original),
      }),
      columnHelper.accessor(getCategoryRedlineDescriptionValue, {
        header: "Description",
        id: "description",
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ table, row }) => renderDescriptionRedline(table, row.original),
      }),
      columnHelper.accessor(getCategoryRedlineFeeValue, {
        header: "Fee",
        id: "fee",
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ table, row }) => renderFeeRedline(table, row.original),
      }),
      columnHelper.accessor(
        (redline) => {
          const category = displayedCategory(redline);
          if (category?.teamMemberIsMasked) return "Restricted";
          return category?.teamMember
            ? teamMemberNames.get(category.teamMember.userId.value) ?? "Team member"
            : "Unassigned";
        },
        {
          header: "Team Member",
          id: "teamMember",
          enableGlobalFilter: true,
          filterFn: "includesString",
          Cell: ({ table, row }) => renderTeamMember(table, row.original),
        },
      ),
      columnHelper.accessor(getCategoryRedlineBillingCodeValue, {
        header: "Billing Code",
        id: "billingCode",
        enableSorting: false,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ table, row }) => renderBillingCodeRedline(table, row.original),
      }),
    ],
    [
      columnHelper,
      disableEditing,
      fieldLockTooltip,
      renderBillingCodeRedline,
      renderDescriptionRedline,
      renderFeeRedline,
      renderNameRedline,
      renderTeamMember,
      teamMemberNames,
    ],
  );

  // #region Row Actions
  const renderRowActions = useCallback(
    (props: { row: MRT_Row<FeeScheduleCategoryRedline> }) => {
      const { row } = props;
      if (!row.original.field.id) return null;

      const category = Object.assign(new FeeScheduleCategoryRedline(row.original.field.id, null, null), row.original);

      if (!category) return null;
      // A row nobody may write to — the viewer is locked out, or the category is one the schedule no
      // longer has — carries the category's own history. Scoped to the category: the schedule as a
      // whole has no history control of its own any more (#4951).
      //
      // A removed category also carries a way back. Its departure is a fact of the negotiation, so the
      // row is a record rather than a decision and nothing here accepts, rejects or undoes it — but a
      // record the schedule can be talked back into is different from a dead end, and restoring is the
      // only write a tombstone offers. A viewer who may not edit at all gets the history alone.
      const isRemovedCategory = isRemovedCategoryRow(category);
      if (disableEditing || isRemovedCategory) {
        return (
          <RowActionsContainer>
            {isRemovedCategory && !disableEditing && (
              <Tooltip title="Restore Fee">
                <span>
                  <ActionButton
                    aria-label="Restore Fee"
                    onClick={() => category.originalEntry && handleRestoreClicked(category.originalEntry)}
                  >
                    <RestoreFromTrashIcon color="secondary" />
                  </ActionButton>
                </span>
              </Tooltip>
            )}
            <ProposalFieldHistoryButton
              field={category.field}
              collectionItemId={row.original.field.id.value}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
            />
          </RowActionsContainer>
        );
      }

      return (
        <RowActionsContainer>
          {category.isResolved && !category.isRemoved && !category.isNewlyAdded && (
            <Tooltip title="Remove Fee">
              <span>
                <ProposalDeleteActionButton
                  aria-label="Remove Fee"
                  onClick={() => category.field.id && handleRemoveClicked(category.field.id)}
                />
              </span>
            </Tooltip>
          )}
          {!category.isResolved && (
            <>
              <Tooltip title="Accept Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.accept()));
                    }}
                  >
                    <CheckIcon color="success" />
                  </ActionButton>
                </span>
              </Tooltip>
              <Tooltip title="Reject Changes">
                <span>
                  <ActionButton
                    onClick={() => {
                      onFeeScheduleRedlineChange(feeScheduleRedline.updateRedline(category.reject()));
                    }}
                  >
                    <CloseIcon color="error" />
                  </ActionButton>
                </span>
              </Tooltip>
            </>
          )}
          {category.canBeUndone && (
            <Tooltip title="Undo Changes">
              <span>
                <ActionButton
                  onClick={() => {
                    if (category.field.id) {
                      onFeeScheduleRedlineChange(feeScheduleRedline.undoRedlineById(category.field.id), category.field);
                    }
                  }}
                >
                  <UndoIcon color="secondary" />
                </ActionButton>
              </span>
            </Tooltip>
          )}
          <ProposalFieldTimelineDropdown
            field={category.field}
            negotiationId={negotiationId}
            viewerScope={viewerScope}
            toolTip="Column change history"
            // A category is negotiated column by column, so its history is browsed and reverted column
            // by column too (#4847) — with the category's own arrivals and departures kept in their own
            // section beneath, which is the whole-schedule tracking the issue asks to keep.
            collectionItemAspects={proposalFeeScheduleAspectOptions}
            onRevert={(entry) => category.field.id && handleCategoryRevert(category.field.id, entry)}
          />
        </RowActionsContainer>
      );
    },
    [
      disableEditing,
      isRemovedCategoryRow,
      negotiationId,
      viewerScope,
      handleRemoveClicked,
      handleCategoryRevert,
      handleRestoreClicked,
      onFeeScheduleRedlineChange,
      feeScheduleRedline,
    ],
  );

  // #region GetRowId
  const getRowId = useCallback((row: FeeScheduleCategoryRedline) => {
    return row.field.id?.value ?? Guid.generate().value;
  }, []);

  // #region muiTableProps
  const muiTableProps = useCallback(() => {
    return ({
      sx: {
        borderSpacing: 0,
      },
    });
  }, []);

  // #region muiTableBodyRowProps
  const muiTableBodyRowProps = useCallback((props: { row: MRT_Row<FeeScheduleCategoryRedline> }) => {
    const { row } = props;
    const isActiveRow = row.original.field?.isEqualTo(activeReviewField);
    const isRemovedCategory = isRemovedCategoryRow(row.original);
    return {
      hover: false,
      onClick: (event: ReactMouseEvent<HTMLTableRowElement>) => {
        if (disableEditing || isRemovedCategory) return;
        if (!row.original.field.id) return;
        const target = event.target;
        if (
          target instanceof Element &&
          target.closest(
            "button, a, input, textarea, select, [role='button'], [contenteditable='true'], .MuiFormControl-root, .MuiInputBase-root",
          )
        ) return;
        onCategoryClicked?.(row.original.field.id);
      },
      sx: {
        ...(isActiveRow && {
          animation: `${pulseRowOutline} 1.5s ease-in-out infinite`,
        }),
        cursor: disableEditing || isRemovedCategory ? "default" : undefined,
      },
    }
  }, [activeReviewField, disableEditing, isRemovedCategoryRow, onCategoryClicked]);

  const muiTableBodyCellProps = useCallback((props: {
    table: MRT_TableInstance<FeeScheduleCategoryRedline>;
    cell: { column: { id: string }; getValue?: () => unknown };
    row: MRT_Row<FeeScheduleCategoryRedline>;
  }) => {
    const { table, cell, row } = props;
    const redlineableFeeScheduleColumns = ["name", "description", "fee", "teamMember", "billingCode"];
    const isRedlineableFeeScheduleColumn = redlineableFeeScheduleColumns.includes(cell.column.id);
    const isActiveRow = row.original.field?.isEqualTo(activeReviewField);
    // The green "resolved" box is for a decision taken in this editor. A category removed earlier in
    // the negotiation has the same shape as an accepted removal, but is a record, not a decision.
    const isResolvedAccepted = (redline: FeeScheduleCategoryRedline | undefined) =>
      redline !== undefined &&
      !isRemovedCategoryRow(redline) &&
      Boolean(redline.wasRedlined) &&
      Boolean(redline.isResolved) &&
      Boolean(redline.isAccepted);
    const showResolvedBackground = isRedlineableFeeScheduleColumn && isResolvedAccepted(row.original);
    const visibleRows = table.getRowModel().rows;
    const visibleRowIndex = visibleRows.findIndex((visibleRow) => visibleRow.id === row.id);
    const previousRow = visibleRows[visibleRowIndex - 1]?.original;
    const nextRow = visibleRows[visibleRowIndex + 1]?.original;
    const previousRowIsResolvedAccepted = isResolvedAccepted(previousRow);
    const nextRowIsResolvedAccepted = isResolvedAccepted(nextRow);
    const isFirstResolvedColumn = cell.column.id === redlineableFeeScheduleColumns[0];
    const isLastResolvedColumn = cell.column.id === redlineableFeeScheduleColumns.at(-1);

    const capDescriptionWidth = cell.column.id === "description" && editingDescriptionRowId !== row.id;

    return {
      sx: {
        ...feeScheduleTableCellStyles(theme, { columnId: cell.column.id }),
        ...(capDescriptionWidth && {
          maxWidth: DESCRIPTION_MAX_WIDTH,
          minWidth: 0,
        }),
        [paneIsCompact(theme)]: {
          padding: theme.spacing(0.5, 1),
          "& .MuiTextField-root": {
            margin: 0,
            minWidth: "6rem",
          },
          "& .MuiInputBase-root": {
            minHeight: 34,
          },
          "& .MuiInputBase-input": {
            paddingBottom: theme.spacing(0.5),
            paddingTop: theme.spacing(0.5),
          },
          "& .MuiFormHelperText-root": {
            marginTop: 0,
          },
        },
        ...(showResolvedBackground && {
          backgroundColor: alpha(green[500], 0.06),
          ...(!previousRowIsResolvedAccepted && {
            borderTop: `1px solid ${green[500]}`,
          }),
          ...(!nextRowIsResolvedAccepted && {
            borderBottom: `1px solid ${green[500]}`,
          }),
          ...(isFirstResolvedColumn && {
            borderLeft: `1px solid ${green[500]}`,
          }),
          ...(isLastResolvedColumn && {
            borderRight: `1px solid ${green[500]}`,
          }),
        }),
        ...(isActiveRow && isRedlineableFeeScheduleColumn && {
          borderTop: "2px solid",
          borderBottom: "2px solid",
          ...(isFirstResolvedColumn && {
            borderLeft: "2px solid",
          }),
          ...(isLastResolvedColumn && {
            borderRight: "2px solid",
          }),
          borderColor: "inherit",
        }),
      },
    };
  }, [activeReviewField, editingDescriptionRowId, isRemovedCategoryRow, theme]);

  // #region muiTableHeadCellProps
  const muiTableHeadCellProps = useCallback(
    (props: { column: { id: string } }) => ({
      sx: props.column.id === "description" ? { maxWidth: DESCRIPTION_MAX_WIDTH, minWidth: 0 } : undefined,
    }),
    [],
  );

  // #region renderEmptyRowsFallback
  const renderEmptyRowsFallback = useMemo(
    () => emptyRowsFallback<FeeScheduleCategoryRedline>({ subject: "fee schedule categories" }),
    [],
  );

  // #region renderTopToolbarCustomActions
  // The two ways a schedule gains a category are one split button now rather than two full-width
  // buttons stacked above the search: "Add New Category" is the press it makes, "Add Existing Category"
  // the alternative behind its dropdown (#4955).
  const addActions = useMemo(
    () =>
      disableEditing
        ? []
        : [
          { label: ADD_NEW_CATEGORY_LABEL, onClick: onCreateCategoryClicked },
          {
            label: ADD_EXISTING_CATEGORY_LABEL,
            icon: <SnippetFolderIcon fontSize="small" />,
            onClick: onExistingCategoryClicked,
          },
        ],
    [disableEditing, onCreateCategoryClicked, onExistingCategoryClicked],
  );

  // The search first, the add control beside it, and the three view buttons at the far end — one
  // wrapping row, so the add control sits to the RIGHT of the search wherever there is room and drops
  // underneath where there is not. No breakpoint decides it; the row breaks when the width says so.
  //
  // #4953: no schedule-wide Accept All / Reject All / Undo Fee Changes here. A category is decided on
  // its own row, and the whole-proposal bulk actions on the active proposal page cover "settle
  // everything at once" — a second, collection-scoped copy of them was clutter.
  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FeeScheduleCategoryRedline> }) => (
      <CollectionToolbar>
        <CollectionToolbarSearch>
          <GlobalFilterTextField table={table} />
        </CollectionToolbarSearch>
        <TableAddActions actions={addActions} />
        <CollectionToolbarViewButtons>
          <ToggleFiltersButton table={table} />
          <ShowHideColumnsButton table={table} />
          <ToggleFullScreenButton table={table} />
        </CollectionToolbarViewButtons>
      </CollectionToolbar>
    ),
    [addActions],
  );
  // #region renderToolbarInternalActions
  // No whole-schedule history here: a category's history lives on its row, and a removed category keeps
  // a row of its own (#4951). Reading the schedule's history beside the categories' was the confusion.
  //
  // An empty fragment rather than `null`: MRT reads this slot's result with `??`, so a nullish return
  // falls straight back to its own default buttons and the page would carry two sets.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  // #region muiTableContainerProps
  // `overflowX: auto` is what makes the remaining overflow reachable at all: every ancestor of this
  // table clips, so before it a column pushed past the viewport was simply gone (#4490). The shaded
  // edges and scroll arrows below make that scrolling evident.
  const muiTableContainerProps = useMemo(
    () => ({
      ref: tableContainerRef,
      sx: {
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        overflowX: "auto",
        overflowY: "auto",
        position: "relative",
      },
    }),
    [tableContainerRef],
  );

  // #region Table Options
  const tableOptions: MRT_TableOptions<FeeScheduleCategoryRedline> = useMemo(
    () => ({
      // Deliberately NOT `resizableColumnOptions()`, which every other collection table this PR
      // touches now takes. Resizing needs MRT's `grid` layout, and this table's cells are not laid out
      // for it: a category's name, description and fee each carry an inline edit control beside the
      // value, and in a grid column sized to a declared width those controls are pushed out of the
      // cell. Giving them room back means re-deciding what each of these cells IS, which is exactly
      // the restructuring #4955's review put out of scope for the tab it named as the reference. What
      // this table takes from that review is everything that does not touch its columns: the split add
      // control, the toolbar's one wrapping row, the one-line description, and the scrollbar that
      // replaced its chevrons.
      columns,
      data: rows,
      enableKeyboardShortcuts: false,
      muiPaginationProps: {
        color: "primary",
        shape: "rounded",
        showRowsPerPage: false,
        variant: "outlined",
      },
      enableRowSelection: false,
      enableTableHead: true,
      getRowId,
      initialState: {
        showColumnFilters: false,
        showGlobalFilter: true,
        columnPinning: { right: ["mrt-row-actions"] },
        density: isMediumDisplaySize ? "compact" : "comfortable",
      },
      enableColumnPinning: true,
      enableColumnFilters: true,
      enableGlobalFilter: true,
      enableGlobalFilterRankedResults: false,
      globalFilterFn: "includesString",
      manualFiltering: false,
      enableStickyHeader: true,
      enableStickyFooter: true,
      manualPagination: true,
      // #4955: the sort is REAL, and `manualSorting` is deliberately absent. It used to be `true`, which
      // tells MRT the rows arrive already ordered and to leave them where they are — while nothing here
      // ever ordered `rows` by the `sorting` state. So every `⋮ → Sort by …` item moved the header
      // arrow and changed nothing, which is worse than offering no sort at all. Client-side sorting on
      // the accessor values is what those menu items already promise, and a schedule is a handful of
      // rows, so MRT sorts them here.
      enableRowActions: true,
      onColumnFiltersChange: setColumnFilters,
      onGlobalFilterChange: setQuery,
      onSortingChange: setSorting,
      rowCount: rows.length,
      state: {
        columnFilters,
        globalFilter: query,
        sorting,
      },
      muiTableProps,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
      renderRowActions: renderRowActions,
      muiTableContainerProps,
      positionGlobalFilter: "none",
      muiTopToolbarProps: {
        sx: {
          // A zero floor on the inner Box is what lets MRT's toolbar grid track clamp to the pane
          // rather than size itself to its content — without it the toolbar, which is
          // `overflow: hidden`, clips whatever runs past the right edge and the one wrapping row
          // inside it never gets the chance to wrap.
          "& > .MuiBox-root": {
            [paneIsCompact(theme)]: {
              flexDirection: "column",
              gap: 0,
              marginLeft: 0,
              padding: 0,
            },
            alignItems: "center",
            flexDirection: "row",
            marginLeft: theme.spacing(1),
            minWidth: 0,
          },
        },
      },
      muiTablePaperProps: {
        elevation: 0,
        sx: {
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        },
      },
      muiTableBodyProps: {
        sx: {
          height: "100%",
          "& tr:only-of-type td[colspan]": {
            height: "100%",
            verticalAlign: "middle",
            padding: 0,
            borderBottom: "none",
          },
        },
      },
      muiSearchTextFieldProps: {
        placeholder: "Search",
        slotProps: {
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  onClick={() => setQuery("")}
                >
                  <CloseIcon />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        },
      },
    }),
    [
      theme,
      columns, 
      rows,
      columnFilters,
      query,
      sorting,
      isMediumDisplaySize,
      getRowId, 
      muiTableProps,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      renderEmptyRowsFallback,
      renderTopToolbarCustomActions, 
      renderToolbarInternalActions, 
      renderRowActions, 
      muiTableContainerProps
    ]
  );

  // #region Render
  return (
    <Root className={className}>
      <MRTNoMemo<FeeScheduleCategoryRedline> options={tableOptions} />
    </Root>
  );
};

/**
 * The category a row shows. A row still in play shows where its decisions leave it; a removed one —
 * struck through, with nothing after the removal — shows the category that was removed.
 */
function displayedCategory(redline: FeeScheduleCategoryRedline): FeeScheduleCategoryEntry {
  return redline.resultingEntry ?? (redline.isRemoved ? redline.revisedEntry ?? redline.originalEntry : null);
}

function teamMemberValue(member: EntityMember): string {
  return `${member.userId.value}:${member.legalEntityId.value}`;
}

/**
 * Every distinct description the cell is showing, in full: for an open redline that is both the
 * previous text and the one being asked for, so the hover answers the same question the clamped cell
 * raises.
 */
function fullDescriptionText(categoryRedline: FeeScheduleCategoryRedline): string {
  const descriptionRedline = categoryRedline.descriptionRedline;
  if (!descriptionRedline) return "";
  const values = [
    descriptionRedline.originalEntry,
    descriptionRedline.revisedEntry,
    descriptionRedline.currentEntry,
  ]
    .map((entry) => entry?.value?.trim())
    .filter((value): value is string => Boolean(value));
  return [...new Set(values)].join("\n");
}

type FeeScheduleCategoryEntry = FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | null | undefined;
type RedlineTextSearchEntry = { value?: string } | null | undefined;
type RedlineTextSearchValue = {
  originalEntry?: RedlineTextSearchEntry;
  revisedEntry?: RedlineTextSearchEntry;
  currentEntry?: RedlineTextSearchEntry;
};

function getTextRedlineSearchValue(redline: RedlineTextSearchValue | undefined): string {
  if (!redline) return "";
  return [
    redline.currentEntry?.value,
    redline.revisedEntry?.value,
    redline.originalEntry?.value,
  ]
    .filter(Boolean)
    .join(" ");
}

function getCategoryRedlineNameValue(categoryRedline: FeeScheduleCategoryRedline): string {
  return getTextRedlineSearchValue(categoryRedline.nameRedline) || getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => entry.name?.value ?? "",
  );
}

function getCategoryRedlineDescriptionValue(categoryRedline: FeeScheduleCategoryRedline): string {
  return getTextRedlineSearchValue(categoryRedline.descriptionRedline) || getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => entry.description?.value ?? "",
  );
}

function getCategoryRedlineBillingCodeValue(categoryRedline: FeeScheduleCategoryRedline): string {
  return getTextRedlineSearchValue(categoryRedline.billingCodeRedline) || getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => entry instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : entry.billingCode?.value ?? "",
  );
}

function getCategoryRedlineFeeValue(categoryRedline: FeeScheduleCategoryRedline): string {
  if (categoryRedline.feeRedline) {
    return [
      getFeeSearchValue(categoryRedline.feeRedline.currentEntry),
      getFeeSearchValue(categoryRedline.feeRedline.revisedEntry),
      getFeeSearchValue(categoryRedline.feeRedline.originalEntry),
    ]
      .filter(Boolean)
      .join(" ");
  }

  return getCategoryEntrySearchValue(
    categoryRedline,
    (entry) => entry instanceof FeeScheduleCategoryMaskedCurrency ? "Masked" : getFeeSearchValue(entry.fee),
  );
}

function getCategoryEntrySearchValue(
  categoryRedline: FeeScheduleCategoryRedline,
  getValue: (entry: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => string,
): string {
  return [
    getCategoryEntryValue(categoryRedline.currentEntry, getValue),
    getCategoryEntryValue(categoryRedline.revisedEntry, getValue),
    getCategoryEntryValue(categoryRedline.originalEntry, getValue),
  ]
    .filter(Boolean)
    .join(" ");
}

function getCategoryEntryValue(
  entry: FeeScheduleCategoryEntry,
  getValue: (entry: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) => string,
): string {
  if (!entry) return "";
  return getValue(entry);
}

function getFeeSearchValue(fee: Fee | null | false | undefined): string {
  if (fee === false || fee === undefined) return "";
  if (fee === null) return "Deferred";
  return [
    fee.toString(),
    fee.rate?.amount?.toString(),
    fee.rate?.currency?.toString(),
    fee.unit ?? "Fixed",
  ]
    .filter(Boolean)
    .join(" ");
}

export default FeeScheduleRedlineSelection;
